import beanzLogoSvg from "@client-shared/assets/beanz-logo.svg";
import keepCupLogoSvg from "@client-shared/assets/keep-cup-logo.svg";

// Rasterize at this multiple of the PDF point size so the embedded image is crisp at 300+ DPI print.
export const LOGO_RASTER_SCALE = 8;

export const BEANZ_LOGO_CACHE_DIMENSIONS = { width: 60, height: 18 } as const;
export const KEEP_CUP_LOGO_CACHE_DIMENSIONS = { width: 38, height: 48 } as const;

interface LogoSpec {
  source: string;
  width: number;
  height: number;
}

// Per-logo dimensions are required so each brand's aspect ratio is preserved when rasterized via canvas.
class LogoCache {
  private cache: Uint8Array | null = null;

  constructor(private readonly spec: LogoSpec) {}

  async getLogo(): Promise<Uint8Array> {
    if (this.cache) {
      return this.cache;
    }
    const logoData = await this.createLogoImage();
    this.cache = logoData;
    return logoData;
  }

  clearCache(): void {
    this.cache = null;
  }

  private async createLogoImage(): Promise<Uint8Array> {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context");
    }

    const scale = LOGO_RASTER_SCALE;
    const { source, width, height } = this.spec;
    canvas.width = width * scale;
    canvas.height = height * scale;

    const img = new Image();
    return new Promise<Uint8Array>((resolve, reject) => {
      img.onload = () => this.handleImageLoad(img, ctx, canvas, scale, resolve, reject);
      img.onerror = reject;
      img.src = source;
    });
  }

  private handleImageLoad(
    img: HTMLImageElement,
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    scale: number,
    resolve: (value: Uint8Array) => void,
    reject: (reason?: Error) => void
  ): void {
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, this.spec.width, this.spec.height);
    canvas.toBlob((blob) => this.handleCanvasBlob(blob, resolve, reject), "image/png");
  }

  private async handleCanvasBlob(blob: Blob | null, resolve: (value: Uint8Array) => void, reject: (reason?: Error) => void): Promise<void> {
    if (!blob) {
      reject(new Error("Failed to convert canvas to blob"));
      return;
    }

    try {
      const arrayBuffer = await blob.arrayBuffer();
      resolve(new Uint8Array(arrayBuffer));
    } catch (error) {
      reject(new Error(`Failed to read blob as ArrayBuffer: ${error instanceof Error ? error.message : "Unknown error"}`));
    }
  }
}

const beanzLogoCache = new LogoCache({ source: beanzLogoSvg, ...BEANZ_LOGO_CACHE_DIMENSIONS });
const keepCupLogoCache = new LogoCache({ source: keepCupLogoSvg, ...KEEP_CUP_LOGO_CACHE_DIMENSIONS });

export const clearBeanzLogoCache = (): void => {
  beanzLogoCache.clearCache();
};

export const createBeanzLogoImage = async (): Promise<Uint8Array> => {
  return beanzLogoCache.getLogo();
};

export const clearKeepCupLogoCache = (): void => {
  keepCupLogoCache.clearCache();
};

export const createKeepCupLogoImage = async (): Promise<Uint8Array> => {
  return keepCupLogoCache.getLogo();
};
